import { type ReactElement, useEffect, useState } from 'react';

import { useAppDispatch } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';
import { animated, useTransition } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { makeStyles } from 'legacyStyles';

import {
  type ConsultationTipImage,
  ImageWithLegend,
} from 'Apps/Consultation/Components/ImageWithLegend';

import SliderControls from 'Components/SliderControls';

import { trackHeapEvent } from 'dux/tracking/actions';

const SliderRoot = styled.div`
  max-width: 100%;
`;

const ImagesContent = styled.div`
  position: relative;

  width: 100%;
  margin-bottom: 20px;
  padding-bottom: 100%;
`;

const ImageContent = styled(animated.div)`
  position: absolute;
  width: 100%;
`;

const SlideOnClick = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;

  width: 40%;

  cursor: pointer;
`;

const TextsContent = styled.div`
  position: relative;
  min-height: 80px;
`;

const SlideText = styled(animated.div)`
  position: absolute;
  width: 100%;
`;

const useStyles = makeStyles({
  controlsRoot: {
    position: 'absolute',
    bottom: 0,
    left: '50%',

    width: '100%',
    marginLeft: '-50%',
  },
});

export type SlideContent = {
  id: string;
  image: ConsultationTipImage;
  content?: ReactElement;
  heapTracker: string;
};

type SliderImageAndContentProps = {
  heapDomain?: string;
  isOnScreen?: boolean;
  slidesContent: SlideContent[];
};

export const SliderImageAndContent = ({
  heapDomain,
  isOnScreen = false,
  slidesContent,
}: SliderImageAndContentProps) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isOnScreen && heapDomain && slidesContent) {
      dispatch(
        trackHeapEvent(
          `${heapDomain} - ${slidesContent[index]?.heapTracker ?? slidesContent[index]?.id} Viewed`,
        ),
      );
    }
  }, [heapDomain, index, isOnScreen]);

  const opacityTransitions = useTransition(index, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  // list images - all slides should have an image, but maybe only one
  const images = slidesContent
    .map(({ image }) => image) // Get `image{}`  only
    .filter((image) => image); // filter out undefined - should not happen

  // list contents - not all slides will get a content for each image
  const contents = slidesContent
    .map(({ content }) => content) // Get `content''` only
    .filter((content) => content); // filter out undefined

  // Direction is either -1 or 1; we will add/remove 1 without mapping 'prev' or 'next' text values
  const slideToDirection = (direction: number): void => {
    const expectedNextIndex = index + direction;
    const nextIndex = Math.max(0, Math.min(expectedNextIndex, images.length - 1));
    setIndex(nextIndex);
  };

  // Gesture handler
  const bind = useDrag(
    ({ swipe: [swipeX] }) => {
      if (swipeX !== 0 /* either -1 or 1 */) {
        // Pulling the content to slide so inverting direction
        slideToDirection(-swipeX);
      }
    },
    {
      swipe: {
        // fine tuned these threshold values while playing with the gesture
        distance: 7,
        velocity: 0.25,
      },
    },
  );

  return (
    <SliderRoot>
      <ImagesContent {...bind()}>
        {opacityTransitions((props, item) => {
          const { id, image } = slidesContent[item] ?? {};
          return (
            image?.src && (
              <ImageContent key={`${id}${image.alt}`} style={props}>
                <ImageWithLegend alt={image?.alt ?? ''} legend={image?.legend} src={image?.src} />
              </ImageContent>
            )
          );
        })}

        {images?.length > 1 && (
          <>
            <SlideOnClick onClick={() => slideToDirection(-1)} style={{ left: 0 }} />
            <SlideOnClick onClick={() => slideToDirection(1)} style={{ right: 0 }} />
            <SliderControls
              classes={{ root: classes.controlsRoot }}
              currentIndex={index}
              onClick={setIndex}
              size={slidesContent?.length || 0}
              theme="rectangleWhite"
            />
          </>
        )}
      </ImagesContent>

      {contents?.length > 0 && (
        <TextsContent>
          {opacityTransitions((props, item) => {
            const { content, id } = slidesContent[item] ?? {};
            return (
              <SlideText key={id} style={props}>
                {content}
              </SlideText>
            );
          })}
        </TextsContent>
      )}
    </SliderRoot>
  );
};
